import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Stack, TextField, Typography, Snackbar, Alert } from '@mui/material';
import notificationsService, { NotificationRecord, TemplateDef } from '../services/notificationsService';
import { useTranslation } from '../i18n/useTranslation';

const NotificationsCenter: React.FC = () => {
  const { t } = useTranslation();
  const [records, setRecords] = useState<NotificationRecord[]>([]);
  const [templates, setTemplates] = useState<TemplateDef[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    channel: 'inapp' as 'email' | 'sms' | 'inapp' | 'multi',
    template: '',
    subject: '',
    message: '',
    audience: { role: 'merchant', customerType: 'individual', status: 'approved', merchantId: '' },
    scheduledAt: ''
  });
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [list, tmpl] = await Promise.all([notificationsService.list(), notificationsService.templates()]);
    setRecords(list);
    setTemplates(tmpl);
  };

  useEffect(() => { load(); }, []);

  const pickTemplate = (key: string) => {
    setForm(prev => {
      const tdef = templates.find(t => t.key === key);
      return { ...prev, template: key, subject: tdef?.subject || '', message: tdef?.message || '' };
    });
  };

  const submit = async () => {
    setError(null);
    if (!form.message || !form.message.trim()) {
      setError('Message is required');
      return;
    }
    try {
      setSubmitting(true);
      await notificationsService.create({ channel: form.channel, template: form.template, subject: form.subject, message: form.message, audience: form.audience, scheduledAt: form.scheduledAt || null });
      setOpen(false);
      setSuccess('Notification queued');
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to send notification';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const preview = async () => {
    const res = await notificationsService.preview(form.audience);
    setPreviewCount(res.estimatedRecipients);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': { position: 'relative', zIndex: 0 },
    '& .MuiOutlinedInput-root.Mui-focused': { zIndex: 0 },
    '& .MuiInputLabel-root': { position: 'relative', zIndex: 1 },
  } as const;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('notifications.title')}</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>{t('notifications.new')}</Button>
      </Stack>

      <Box sx={{ borderRadius: 2, background: 'white', p: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        {records.map(n => (
          <Box key={n.id} sx={{ py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip label={n.channel.toUpperCase()} color={n.channel === 'sms' ? 'warning' : n.channel === 'email' ? 'primary' : n.channel === 'multi' ? 'secondary' : 'default'} />
              <Typography variant="subtitle2">{n.subject || t('notifications.noSubject')}</Typography>
              <Chip label={n.status} color={n.status === 'queued' ? 'info' : n.status === 'sent' ? 'success' : 'error'} size="small" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{new Date(n.createdAt).toLocaleString()}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{n.message}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Audience: {JSON.stringify(n.audience)}</Typography>
            <Divider sx={{ mt: 1 }} />
          </Box>
        ))}
        {records.length === 0 && (
          <Typography variant="body2" color="text.secondary">{t('message.noData')}</Typography>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('notifications.new')}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, pt: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('notifications.channel')}</Typography>
            <TextField fullWidth select value={form.channel} onChange={e => setForm(prev => ({ ...prev, channel: e.target.value as any }))} sx={textFieldSx}>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="inapp">In-App</MenuItem>
            <MenuItem value="multi">Multi</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('notifications.template')}</Typography>
            <TextField fullWidth select value={form.template} onChange={e => pickTemplate(e.target.value)} sx={textFieldSx} placeholder={t('notifications.template')}>
              <MenuItem value="">{t('notifications.custom')}</MenuItem>
              {templates.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
            </TextField>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('notifications.subject')}</Typography>
            <TextField fullWidth value={form.subject} onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))} sx={textFieldSx} placeholder={t('notifications.subject')} />
          </Box>
          <Box sx={{ gridColumn: '1 / span 2' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('notifications.message')}</Typography>
            <TextField fullWidth value={form.message} onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))} multiline minRows={4} sx={{ ...textFieldSx }} placeholder={t('notifications.message')} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('notifications.scheduleAt')}</Typography>
            <TextField fullWidth type="datetime-local" value={form.scheduledAt} onChange={e => setForm(prev => ({ ...prev, scheduledAt: e.target.value }))} sx={textFieldSx} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" onClick={preview}>{t('notifications.preview')}</Button>
            {previewCount !== null && (
              <Typography variant="body2" color="text.secondary">{t('notifications.estimatedRecipients').replace('{n}', String(previewCount))}</Typography>
            )}
          </Box>
          <Typography variant="subtitle2" sx={{ gridColumn: '1 / span 2' }}>{t('notifications.audience')}</Typography>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('settings.role')}</Typography>
            <TextField fullWidth select value={form.audience.role} onChange={e => setForm(prev => ({ ...prev, audience: { ...prev.audience, role: e.target.value } }))} sx={textFieldSx}>
              <MenuItem value="merchant">MERCHANT</MenuItem>
              <MenuItem value="wave">WAVE</MenuItem>
              <MenuItem value="bank">BANK</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('form.customerType')}</Typography>
            <TextField fullWidth select value={form.audience.customerType} onChange={e => setForm(prev => ({ ...prev, audience: { ...prev.audience, customerType: e.target.value } }))} sx={textFieldSx}>
              <MenuItem value="individual">{t('customerType.individual')}</MenuItem>
              <MenuItem value="sme">{t('customerType.sme')}</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('common.status')}</Typography>
            <TextField fullWidth select value={form.audience.status} onChange={e => setForm(prev => ({ ...prev, audience: { ...prev.audience, status: e.target.value } }))} sx={textFieldSx}>
              <MenuItem value="approved">{t('status.approved')}</MenuItem>
              <MenuItem value="pending">{t('status.pending')}</MenuItem>
              <MenuItem value="rejected">{t('status.rejected')}</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>{t('bank.enterMerchantId')}</Typography>
            <TextField fullWidth value={form.audience.merchantId} onChange={e => setForm(prev => ({ ...prev, audience: { ...prev.audience, merchantId: e.target.value } }))} sx={textFieldSx} placeholder={t('bank.enterMerchantId')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>{t('notifications.send')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsCenter;


